import React, { useCallback, useEffect, useRef, useState } from "react";

import { FilterCategory, ProjectSearchAppProps, SearchResponse, SearchResultItem, SearchSortOption } from "./types";
import { buildSearchUrl, buildBrowserSearch, readBrowserState, normalizeSelectedFilters } from "./searchUtils";
import { SearchMap } from "./SearchMap";
import { ResultItem } from "./ResultItem";
import { FilterSection } from "./FilterSection";
import { ActiveFiltersBar, ActiveFilterItem } from "./ActiveFiltersBar";

const PROJECT_SEARCH_RESULT_LIMIT = 1000;

// ── Huvudkomponent ─────────────────────────────────────────────────────────────

export const ProjectSearchApp: React.FC<ProjectSearchAppProps> = ({
  searchScope,
  searchApiUrl,
  filterCategories,
  initialQuery,
  initialSelectedFilters,
  initialTagFilters,
  initialHits,
  initialResults,
  initialSortOptions,
  initialSort,
  includePagination,
  pageSize,
  searchIconSvg,
  crossIconSvg,
  heading,
  placeholderText,
  searchButtonText = "Sök",
}) => {
  const initialStateRef = useRef({
    query: initialQuery ?? "",
    selectedFilters: normalizeSelectedFilters(initialSelectedFilters ?? {}),
    tagFilters: [...(initialTagFilters ?? [])].sort(),
    hits: typeof initialHits === "number" ? initialHits : null,
    results: initialResults ?? [],
    sort: initialSort ?? "",
    sortOptions: initialSortOptions ?? [],
  });
  const normalizedInitialSelectedFilters = initialStateRef.current.selectedFilters;
  const normalizedInitialTagFilters = initialStateRef.current.tagFilters;
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const historyReadyRef = useRef(false);

  const [query, setQuery] = useState(initialStateRef.current.query);
  const [selected, setSelected] = useState<Record<string, string[]>>(normalizedInitialSelectedFilters);
  const [results, setResults] = useState<SearchResultItem[]>(initialStateRef.current.results);
  const [hits, setHits] = useState<number | null>(initialStateRef.current.hits);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [searchedQuery, setSearchedQuery] = useState(initialStateRef.current.query);
  const [selectedSort, setSelectedSort] = useState(initialStateRef.current.sort);
  const [sortOptions, setSortOptions] = useState<SearchSortOption[]>(initialStateRef.current.sortOptions);
  const [tagFilters, setTagFilters] = useState<string[]>(normalizedInitialTagFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const currentSearchStateRef = useRef({ query, selected, tagFilters, selectedSort, results });
  currentSearchStateRef.current = { query, selected, tagFilters, selectedSort, results };

  const effectivePageSize = pageSize > 0 ? pageSize : 10;

  const fetchResults = useCallback(
    async (q: string, sel: Record<string, string[]>, tags: string[], sort: string, offset = 0, requestedPageSize = effectivePageSize) => {
      setIsLoading(true);
      setError(null);
      try {
        const url = buildSearchUrl(searchApiUrl, searchScope, q, sel, tags, sort, 0, PROJECT_SEARCH_RESULT_LIMIT);
        const res = await fetch(url, { headers: { Accept: "application/json" } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: SearchResponse = await res.json();
        const hitCount: number = data.hits ?? 0;
        const responseSort = data.sortOptions?.find((option) => option.selected)?.value;
        const configuredSortOptions = initialStateRef.current.sortOptions;
        const nextSortOptions = configuredSortOptions.length > 0
          ? configuredSortOptions.map((configuredOption) => ({
            ...configuredOption,
            selected: configuredOption.value === (responseSort ?? sort),
          }))
          : data.sortOptions ?? [];
        const nextPage = Math.floor(offset / requestedPageSize) + 1;
        setResults(data.pages ?? []);
        setSortOptions(nextSortOptions);
        setSelectedSort(responseSort ?? sort);
        setHits(hitCount);
        setSearchedQuery(q);
        setCurrentPage(nextPage > 0 ? nextPage : 1);
      } catch {
        setError("Söktjänsten svarade inte, försök igen.");
        setStatusMessage("");
      } finally {
        setIsLoading(false);
      }
    },
    [effectivePageSize, searchApiUrl, searchScope],
  );

  const syncBrowserUrl = useCallback((
    q: string,
    sel: Record<string, string[]>,
    tags: string[],
    sort: string,
    mode: "push" | "replace" = "push",
    offset = 0,
    requestedPageSize = effectivePageSize,
  ) => {
    if (typeof window === "undefined") return;
    const nextSearch = buildBrowserSearch(q, sel, tags, sort, offset, requestedPageSize);
    if (window.location.search === nextSearch) return;
    const nextUrl = `${window.location.pathname}${nextSearch}${window.location.hash}`;
    if (mode === "replace") {
      window.history.replaceState(null, "", nextUrl);
      return;
    }
    window.history.pushState(null, "", nextUrl);
  }, [effectivePageSize]);

  const triggerSearch = useCallback(
    (q: string, sel: Record<string, string[]>, tags: string[], sort: string, offset = 0, requestedPageSize = effectivePageSize) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => fetchResults(q, sel, tags, sort, offset, requestedPageSize), 300);
    },
    [effectivePageSize, fetchResults],
  );

  const totalPages = includePagination ? Math.max(1, Math.ceil(results.length / effectivePageSize)) : 1;

  useEffect(() => {
    if (!searchScope || typeof window === "undefined") return;

    const applyStateFromLocation = () => {
      const browserState = readBrowserState(window.location.search);
      const normalizedBrowserSelected = normalizeSelectedFilters(browserState.selected);
      const normalizedBrowserTags = [...browserState.tagFilters].sort();
      const browserSort = browserState.sort || initialStateRef.current.sort;
      const currentSearchState = currentSearchStateRef.current;
      const browserStateMatchesCurrentSearch =
        browserState.query === currentSearchState.query
        && JSON.stringify(normalizedBrowserSelected) === JSON.stringify(normalizeSelectedFilters(currentSearchState.selected))
        && JSON.stringify(normalizedBrowserTags) === JSON.stringify([...currentSearchState.tagFilters].sort())
        && browserSort === currentSearchState.selectedSort;
      const browserStateMatchesServerState =
        browserState.query === initialStateRef.current.query
        && JSON.stringify(normalizedBrowserSelected) === JSON.stringify(normalizedInitialSelectedFilters)
        && JSON.stringify(normalizedBrowserTags) === JSON.stringify(normalizedInitialTagFilters)
        && browserSort === initialStateRef.current.sort
        && browserState.offset === 0;

      if (browserStateMatchesCurrentSearch && currentSearchState.results.length > 0) {
        const requestedPageSize = browserState.hits > 0 ? browserState.hits : effectivePageSize;
        const pageFromOffset = Math.floor(Math.max(browserState.offset, 0) / requestedPageSize) + 1;
        setCurrentPage(pageFromOffset > 0 ? pageFromOffset : 1);
      } else if (!browserStateMatchesServerState || initialStateRef.current.results.length === 0) {
        const offset = Number.isFinite(browserState.offset) ? Math.max(browserState.offset, 0) : 0;
        const requestedPageSize = browserState.hits > 0 ? browserState.hits : effectivePageSize;
        const pageFromOffset = Math.floor(offset / requestedPageSize) + 1;
        setQuery(browserState.query);
        setSelected(normalizedBrowserSelected);
        setTagFilters(browserState.tagFilters);
        setSelectedSort(browserSort);
        setCurrentPage(pageFromOffset > 0 ? pageFromOffset : 1);
        fetchResults(browserState.query, normalizedBrowserSelected, browserState.tagFilters, browserSort, offset, requestedPageSize);
      }

      historyReadyRef.current = true;
    };

    applyStateFromLocation();
    window.addEventListener("popstate", applyStateFromLocation);
    return () => { window.removeEventListener("popstate", applyStateFromLocation); };
  }, [fetchResults, normalizedInitialSelectedFilters, normalizedInitialTagFilters, searchScope]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages > 0 ? totalPages : 1);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (isLoading || hits === null) return;
    const queryLabel = query.trim() && query.trim() !== "*" ? ` för sökningen "${query.trim()}"` : "";
    const filterLabel = activeFilters.length > 0
      ? ` med valda filter: ${activeFilters.map((f) => f.label).join(", ")}`
      : "";
    setStatusMessage(hits === 0
      ? `Inga träffar${queryLabel}${filterLabel}`
      : `${hits} träffar hittades${queryLabel}${filterLabel}`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, hits]);

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    if (historyReadyRef.current) syncBrowserUrl(val, selected, tagFilters, selectedSort, "replace", 0, effectivePageSize);
  };

  const handleFilterChange = (category: FilterCategory, values: string[]) => {
    const next = { ...selected, [category.label]: values };
    if (values.length === 0) delete next[category.label];
    setSelected(next);
    setCurrentPage(1);
    if (historyReadyRef.current) syncBrowserUrl(query, next, tagFilters, selectedSort, "push", 0, effectivePageSize);
    triggerSearch(query, next, tagFilters, selectedSort, 0, effectivePageSize);
  };

  const clearFilters = () => {
    setSelected({});
    setTagFilters([]);
    setCurrentPage(1);
    if (historyReadyRef.current) syncBrowserUrl(query, {}, [], selectedSort, "push", 0, effectivePageSize);
    triggerSearch(query, {}, [], selectedSort, 0, effectivePageSize);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(1);
    if (historyReadyRef.current) syncBrowserUrl(query, selected, tagFilters, selectedSort, "push", 0, effectivePageSize);
    fetchResults(query, selected, tagFilters, selectedSort, 0, effectivePageSize);
  };

  const removeSelectedFilter = (category: FilterCategory, value: string) => {
    const nextValues = (selected[category.label] ?? []).filter((v) => v !== value);
    const nextSelected = { ...selected, [category.label]: nextValues };
    if (nextValues.length === 0) delete nextSelected[category.label];
    setSelected(nextSelected);
    setCurrentPage(1);
    if (historyReadyRef.current) syncBrowserUrl(query, nextSelected, tagFilters, selectedSort, "push", 0, effectivePageSize);
    fetchResults(query, nextSelected, tagFilters, selectedSort, 0, effectivePageSize);
  };

  const removeTagFilter = (value: string) => {
    const nextTags = tagFilters.filter((v) => v !== value);
    setTagFilters(nextTags);
    setCurrentPage(1);
    if (historyReadyRef.current) syncBrowserUrl(query, selected, nextTags, selectedSort, "push", 0, effectivePageSize);
    fetchResults(query, selected, nextTags, selectedSort, 0, effectivePageSize);
  };

  const changeSort = (sort: string) => {
    setSelectedSort(sort);
    setCurrentPage(1);
    if (historyReadyRef.current) syncBrowserUrl(query, selected, tagFilters, sort, "push", 0, effectivePageSize);
    fetchResults(query, selected, tagFilters, sort, 0, effectivePageSize);
  };

  const changePage = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    const offset = (page - 1) * effectivePageSize;
    setCurrentPage(page);
    if (historyReadyRef.current) syncBrowserUrl(query, selected, tagFilters, selectedSort, "push", offset, effectivePageSize);
    if (typeof window !== "undefined") {
      document.getElementById("search-block")?.scrollIntoView({ behavior: "auto", block: "start" });
    }
  };

  const getVisiblePages = (): Array<number | "ellipsis"> => {
    if (totalPages <= 1) return [1];
    const pages: Array<number | "ellipsis"> = [1];
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    if (start > 2) pages.push("ellipsis");
    for (let page = start; page <= end; page += 1) pages.push(page);
    if (end < totalPages - 1) pages.push("ellipsis");
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  };

  const currentSort = selectedSort || sortOptions.find((option) => option.selected)?.value;
  const visibleResults = includePagination
    ? results.slice((currentPage - 1) * effectivePageSize, currentPage * effectivePageSize)
    : results;
  const trimmedSearchedQuery = searchedQuery.trim();
  const searchSummaryText = trimmedSearchedQuery && trimmedSearchedQuery !== "*"
    ? `Du sökte på "${trimmedSearchedQuery}"`
    : "Du sökte på *";
  const hasActiveFilters = Object.values(selected).some((values) => values.length > 0) || tagFilters.length > 0;
  const activeFilters: ActiveFilterItem[] = [
    ...filterCategories.flatMap((category) =>
      category.options
        .filter((option) => (selected[category.label] ?? []).includes(option.value))
        .map((option) => ({
          id: `${category.label}-${option.value}`,
          label: option.text.trim(),
          remove: () => removeSelectedFilter(category, option.value),
        })),
    ),
    ...tagFilters.map((value) => ({
      id: `tag-${value}`,
      label: value,
      remove: () => removeTagFilter(value),
    })),
  ];
  const paginationItems = getVisiblePages();

  return (
    <section className={isLoading ? " loading " : ""}>
      <form className="search-control-block project-search-react__search-block" id="search-block" onSubmit={handleSubmit}>
        {heading && <h2 className="block__heading">{heading}</h2>}
        <div className="grid">
          <div className="g-col-12">
            <div className="search-control-block__search-box-button-container">
              <div className="search-control-block__search-input-autocomplete-wrapper">
                <input
                  id="search-query"
                  name="SearchQuery"
                  type="search"
                  value={query}
                  onChange={handleQueryChange}
                  aria-label={placeholderText ?? searchButtonText}
                  placeholder={placeholderText ?? searchButtonText}
                />
              </div>
              <a
                className="skip-link-search button-secondary ignore-link-focus"
                href="#search-results"
                aria-label="Gå direkt till sökresultatet"
              >
                Gå direkt till sökresultatet
              </a>
              <button type="submit" name="button">
                     {searchIconSvg && (
                    <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <use href={searchIconSvg} />
                    </svg>
                  )}
                <span>{searchButtonText}</span>
              </button>
            </div>
          </div>
        </div>

        {filterCategories.length > 0 && (
          <div className="grid project-search-react__filter-form">
            <div className="g-col-12">
              <div className="project-search-react__filter-sections">
                {filterCategories.map((category, index) => (
                  <FilterSection
                    key={`${category.label}-${index}`}
                    category={category}
                    selectedValues={selected[category.label] ?? []}
                    isLoading={isLoading}
                    onChange={handleFilterChange}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {hasActiveFilters && (
          <ActiveFiltersBar activeFilters={activeFilters} onClearAll={clearFilters} crossIconSvg={crossIconSvg} />
        )}
      </form>

      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {statusMessage}
      </div>

      <div className="grid project-search-react__toggle-row">
        <div className="g-col-12">
          {hits !== null && (
            <div className="project-search-react__results-header">
              <div className="project-search-react__summary">
                <strong>{hits.toLocaleString("sv-SE")} träffar</strong>
                <span>{searchSummaryText}</span>
              </div>

              {sortOptions.length > 0 && (
                <>
                  <div className="project-search-react__mobile-sort">
                    <label htmlFor="project-search-mobile-sort">Sortera efter:</label>
                    <select
                      id="project-search-mobile-sort"
                      value={currentSort ?? ""}
                      onChange={(event) => changeSort(event.target.value)}
                    >
                      {sortOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="vgr-searchservice__resultsorting vgr-searchservice__resultsorting-mobile" role="group" aria-labelledby="project-search-sort-label">
                    <span id="project-search-sort-label">Sortera efter:</span>{" "}
                    {sortOptions.map((option, index) => (
                      <React.Fragment key={option.value}>
                        <button
                          type="button"
                          className="vgr-searchservice__linkbutton"
                          aria-label={`Sortera efter ${option.label}`}
                          aria-pressed={currentSort === option.value}
                          disabled={currentSort === option.value}
                          onClick={() => {
                            changeSort(option.value);
                          }}
                        >
                          {option.label}
                        </button>
                        {index + 1 < sortOptions.length && " | "}
                      </React.Fragment>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid project-search-react__layout">
        <div id="search-results" className="g-col-12 g-col-lg-6 project-search-react__results-column" aria-busy={isLoading}>
          {error && <p role="alert">{error}</p>}

          {results.length > 0 && (
            <section data-index-exclude className="list-block tagmanager-page-listing project-search-react__result-list" aria-labelledby="section-search-hits-heading">
              <h3 id="section-search-hits-heading" className="sr-only">Sökträffar</h3>
              <ul>
                {visibleResults.map((item, index) => (
                  <ResultItem key={`${item.id}-${index}`} item={item} />
                ))}
              </ul>
            </section>
          )}

          {results.length > 0 && includePagination && totalPages > 1 && (
            <>
              <ul aria-label="Paginering" className="pagination search-result-block__pagination">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <a
                href="#search-block"
                className="ignore-link-focus page-link"
                tabIndex={currentPage === 1 ? -1 : 0}
                aria-disabled={currentPage === 1}
                onClick={(e) => {
                  e.preventDefault();
                  changePage(currentPage - 1);
                }}
              >
                <svg className="svg-prev" xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
                  <path d="M4.2644 7.19313C3.81812 7.63941 3.81812 8.36416 4.2644 8.81044L11.1192 15.6653C11.5655 16.1116 12.2903 16.1116 12.7366 15.6653C13.1828 15.219 13.1828 14.4943 12.7366 14.048L6.68859 8L12.733 1.95202C13.1793 1.50575 13.1793 0.780989 12.733 0.334709C12.2867 -0.11157 11.562 -0.11157 11.1157 0.334709L4.26083 7.18956L4.2644 7.19313Z" fill="#005B89" />
                </svg>
                <span className="prevnext desktop-only-text">Föregående</span>
              </a>
            </li>

            {paginationItems.map((item, index) => (
              item === "ellipsis" ? (
                <li key={`ellipsis-${index}`} className="page-item disabled">
                  <span className="ignore-link-focus page-link">...</span>
                </li>
              ) : (
                <li key={`page-${item}`} className={`page-item ${item === currentPage ? "active" : ""}`}>
                  <a
                    href="#search-block"
                    className="page-link"
                    onClick={(e) => {
                      e.preventDefault();
                      changePage(item);
                    }}
                  >
                    {item}
                  </a>
                </li>
              )
            ))}

            <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
              <a
                href="#search-block"
                className="ignore-link-focus page-link"
                tabIndex={currentPage === totalPages ? -1 : 0}
                aria-disabled={currentPage === totalPages}
                onClick={(e) => {
                  e.preventDefault();
                  changePage(currentPage + 1);
                }}
              >
                <span className="prevnext desktop-only-text">Nästa</span>
                <svg className="svg-next" xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
                  <path d="M12.7366 7.19313C13.1828 7.63941 13.1828 8.36416 12.7366 8.81044L5.88171 15.6653C5.43543 16.1116 4.71068 16.1116 4.2644 15.6653C3.81812 15.219 3.81812 14.4943 4.2644 14.048L10.3124 8L4.26797 1.95202C3.82169 1.50575 3.82169 0.780988 4.26797 0.334709C4.71425 -0.11157 5.439 -0.11157 5.88528 0.334709L12.7401 7.18956L12.7366 7.19313Z" fill="#005B89" />
                </svg>
              </a>
            </li>
              </ul>
              <input type="hidden" className="totalHits" value={hits ?? 0} />
            </>
          )}

          {!isLoading && results.length === 0 && hits !== null && hits === 0 && (
            <span>Inga träffar</span>
          )}
        </div>

        <div className="g-col-12 g-col-lg-6 project-search-react__map-column">
          {typeof window !== "undefined" && results.length > 0 && (
            <SearchMap items={results} />
          )}
        </div>
      </div>
    </section>
  );
};
